/**
 * Centralized application initialization manager.
 * Handles multiple initialization tasks (i18next, CSRF setup, etc.) and provides callbacks for when the setup is done.
 */

import HttpBackend from 'i18next-http-backend';
import i18next from 'i18next';
import { localizeSubtree } from './i18nDom.js';
import { util } from './utilities.js';
import './utilitiesMath.js';

export class AppManager {
  constructor() {
    this.isReady = false;
    this.readyCallbacks = [];
    this.initPromise = null;
    this.initTasks = [];
  }

  /**
   * Add an initialization task to be executed during page setup.
   * @param {string} name - Name of the task for debugging purposes.
   * @param {Function} taskFunction - Function that returns a Promise or executes synchronously.
   */
  addInitTask(name, taskFunction) {
    if (this.initPromise) {
      console.warn(`Cannot add init task '${name}' - initialization already started`);
      return;
    }

    this.initTasks.push({ name, taskFunction });
  }

  /**
   * Initialize all registered tasks and built-in page setup.
   * @param {string} csrfToken - The CSRF token to attach to outgoing fetch requests.
   * @param {Parameters<AppManager['_setupI18next']>[0]} i18nextParams - Parameters for i18next initialization.
   * @param {object} [globals] - Map of variable names to values to attach to `window` for global access.
   * @returns {Promise} Promise that resolves when all initialization is complete.
   */
  init(csrfToken, i18nextParams, globals = {}) {
    // Prevent multiple initializations.
    if (this.initPromise) {
      return this.initPromise;
    }

    // Attach globals to `window` synchronously so they're available to subsequent init tasks and page scripts.
    this._setupGlobals(globals);

    // CSRF token setup for fetch requests.
    this.addInitTask('csrf-setup', () => {
      return this._setupCSRF(csrfToken);
    });

    // i18next initialization.
    this.addInitTask('i18next', () => {
      return this._setupI18next(i18nextParams);
    });

    // Set up logWebpageActivity function.
    this.addInitTask('logging-setup', () => {
      return this._setupLogging();
    });

    // Execute all initialization tasks.
    this.initPromise = this._executeInitTasks()
      .then(() => {
        this.isReady = true;
        this._executeReadyCallbacks();
      })
      .catch((error) => {
        console.error('Application initialization failed:', error);
        throw error;
      });

    return this.initPromise;
  }

  /**
   * Register a callback to be executed when the page setup is fully finished.
   * If the setup is already done, the callback is executed immediately.
   * @param {Function} callback - Function to execute when ready.
   */
  ready(callback) {
    if (typeof callback !== 'function') {
      console.warn('AppManager.ready() expects a function as argument');
      return;
    }

    if (this.isReady) {
      callback(); // Page setup is already done, execute callback immediately.
    } else {
      this.readyCallbacks.push(callback); // Queue the callback for when the page becomes ready.
    }
  }

  /**
   * Attach the given key/value pairs to the `window` object so they're accessible from any script on the page.
   * @param {object} globals - Map of global variable names to their values.
   * @private
   */
  _setupGlobals(globals) {
    for (const [key, value] of Object.entries(globals)) {
      window[key] = value;
    }
  }

  /**
   * Set up CSRF token for all fetch requests.
   * @param {string} csrfToken - The token Play's CSRF filter expects in the `Csrf-Token` header.
   * @private
   */
  _setupCSRF(csrfToken) {
    // Set up CSRF token for fetch requests by overwriting the fetch function. The token is only attached to
    // same-origin requests: Play's CSRF filter only checks requests to our own server, and a token signed by this
    // server is meaningless to anyone else. Attaching it to cross-origin requests (Mapbox, Mapillary, Infra3d,
    // sibling city servers, ...) does nothing useful and forces an unnecessary CORS preflight that some
    // hosts reject, so we skip them rather than maintaining an allowlist of hosts to exclude (#4232).
    const originalFetch = window.fetch;
    window.fetch = function (url, options = {}) {
      // `fetch` accepts a string, a `URL`, or a `Request`. Only a `Request` carries its address on `.url`; a `URL`
      // has to be stringified. Reading `.url` off a `URL` gives undefined, which resolves to <origin>/undefined and
      // so reads as same-origin -- meaning a cross-origin `URL` would be handed a token, the very case above. The
      // `typeof` guard is for non-browser hosts (jsdom); any browser with `fetch` also has `Request`.
      const isRequest = typeof Request !== 'undefined' && url instanceof Request;
      const requestUrl = isRequest ? url.url : String(url);

      // Resolve against the page origin so relative URLs (our routes) are correctly treated as same-origin.
      let isSameOrigin;
      try {
        isSameOrigin = new URL(requestUrl, window.location.origin).origin === window.location.origin;
      } catch {
        // If the URL can't be parsed, leave the request untouched rather than guessing.
        isSameOrigin = false;
      }

      if (!isSameOrigin) {
        return originalFetch(url, options);
      }

      // `new Headers` accepts every form `headers` can take; spreading a `Headers` instance would copy nothing.
      let headers;
      try {
        headers = new Headers(options.headers ?? (isRequest ? url.headers : undefined));
      } catch (error) {
        // Fail the promise, not the call, as the browser's fetch does.
        return Promise.reject(error);
      }
      headers.set('Csrf-Token', csrfToken);
      return originalFetch(url, { ...options, headers });
    };
  }

  /**
   * Initialize i18next with the given language and namespaces. Handles country-specific overrides.
   *
   * @param {object} params - Properties that determine which translations should be loaded.
   * @param {string} params.language - The language to use for translations, e.g., "en", "en-US", "es", etc.
   * @param {Array<string>} params.supportedLanguages - Every language the site offers, e.g., ["en", "pt-BR"]
   * @param {Array<string>} params.localeFiles - Every translation file there is, e.g., ["locales/en/common.json"]
   * @param {string} params.defaultNS - The default namespace to use if no specific ns is provided, e.g., "common"
   * @param {Array<string>} params.namespaces - An array of namespaces to load, e.g., ["common", "explore"]
   * @param {string} params.countryId - The server's country ID to determine if we load country-specific overrides
   * @param {object} params.unitWords - The request's distance words (unitAbbr, unitAbbrSmall, unitName,
   *   unitNameSingular), resolved server-side for its language and measurement system.
   * @returns {Promise} Promise that resolves when i18next is ready.
   * @private
   */
  _setupI18next(params) {
    // Set up country-specific namespace overrides.
    let namespaces = params.namespaces;
    if (params.countryId === 'india') {
      namespaces = [...namespaces, ...namespaces.map((str) => `${str}-india`)];
    } else if (params.countryId === 'switzerland') {
      namespaces = [...namespaces, ...namespaces.map((str) => `${str}-zurich`)];
    }

    const localeFiles = new Set(params.localeFiles);
    // With no list, every file is skipped and the page silently shows raw keys, so make that loud.
    if (localeFiles.size === 0) console.error('AppManager: no locale files listed, so nothing will be translated');
    return i18next.use(HttpBackend).init({
      backend: {
        // i18next asks for every namespace in every language, but most country overrides exist only in English, so
        // skip the files that aren't there (an empty path loads nothing) rather than request a 404 (#5570). The ones
        // that are go through util.assetPath, so a deploy never pairs new JS with stale cached translations (#5336).
        loadPath: ([lng], [ns]) =>
          (localeFiles.has(`locales/${lng}/${ns}.json`) ? util.assetPath(`locales/${lng}/${ns}.json`) : ''),
      },
      fallbackLng: 'en',
      // Stops i18next asking for languages we don't have: for pt-BR it would otherwise also try plain "pt".
      supportedLngs: params.supportedLanguages,
      ns: namespaces,
      defaultNS: params.defaultNS,
      lng: params.language,
      interpolation: {
        // Every string may write {{unitName}}, {{unitAbbr}}, … and get this reader's units with no argument at the
        // call site, so there is nothing a caller can forget and no metric/imperial pair of keys to keep in sync.
        // These must be plain values: a getter that called i18next.t() here would recurse through interpolation.
        defaultVariables: params.unitWords,
        // Interpolated values reach a text node, an aria-label, a title or a confirm() far more often than they
        // reach innerHTML, and there HTML-escaping is what the reader sees: a neighborhood called Al 'Ummah prints
        // as "Al &#39;Ummah" and a formatted date as "9&#x2F;16&#x2F;2026" (#5389). So values are interpolated
        // verbatim, and the far smaller set of calls that build markup says so at the call site with
        // `interpolation: { escapeValue: true }`. The `ps/i18n-escape-in-markup` ESLint rule enforces that;
        // docs/internationalization.md has the rule for the flows it cannot see.
        escapeValue: false,
      },
    }, (err) => {
      // Registered before the error check: the formatter doesn't depend on any translation having loaded, and a page
      // whose namespaces failed should still render its distances converted and labeled rather than as raw meters.
      this._addDistanceFormatter();

      // Keep going: one file failing shouldn't leave the rest of the page untranslated.
      if (err?.length > 0) console.error(err);

      // After loading, merge the country-specific override namespaces into the base ones.
      // Going through list of languages so that we still get the en translations when using en-US.
      for (const l of i18next.languages) {
        for (const ns of namespaces) {
          if (params.countryId === 'india') {
            i18next.addResourceBundle(l, ns, i18next.getResourceBundle(l, `${ns}-india`) || {}, true, true);
          } else if (params.countryId === 'switzerland') {
            i18next.addResourceBundle(l, ns, i18next.getResourceBundle(l, `${ns}-zurich`) || {}, true, true);
          }
        }
      }

      // Localize any static [data-i18n] markup on the page now that translations are loaded.
      localizeSubtree(document.body);
    });
  }

  /**
   * Registers the `distance` i18next formatter, which renders a distance the way this reader should see it.
   *
   * Converting, rounding, and locale-formatting a distance is one job, so a string says `{{meters, distance}}` and
   * gets all three rather than each call site repeating the metersToFeet / roundToTwentyFive / format-number dance.
   * The input is always canonical — meters for `small`, kilometers for `large` — never a pre-converted value.
   *
   * Params: `style` (`small` → m/ft rounded to the nearest 25, our convention for mission-scale distances; `fine` →
   * m/ft unrounded, for an elevation, a rise or a short street, which a nearest-25 would flatten to nothing; `large`
   * → km/mi), `precision` (decimal places, `fine` and `large` only), and `unit: false` to emit the bare number for a
   * string that names the unit once across several values. Separate multiple params with `;`.
   *
   * @private
   */
  _addDistanceFormatter() {
    i18next.services.formatter.add('distance', (value, lng, options) => {
      const metric = util.isMetric();
      const fine = options.style === 'fine';
      const small = fine || options.style === 'small';
      let amount;
      if (fine) {
        amount = metric ? value : util.math.metersToFeet(value);
      } else if (small) {
        amount = util.math.roundToTwentyFive(metric ? value : util.math.metersToFeet(value));
      } else {
        amount = metric ? value : util.math.kmsToMiles(value);
      }
      // Nearest-25 rounding leaves no decimals to show; `fine` and `large` take a precision.
      const digits = small && !fine ? 0 : Number(options.precision) || 0;
      const number = new Intl.NumberFormat(lng, {
        minimumFractionDigits: digits, maximumFractionDigits: digits,
      }).format(amount);
      if (options.unit === false) return number;
      const unitWords = i18next.options.interpolation.defaultVariables;
      return `${number} ${small ? unitWords.unitAbbrSmall : unitWords.unitAbbr}`;
    });
  }

  /**
   * Set up logWebpageActivity function to be used anywhere on the site.
   * @private
   */
  _setupLogging() {
    // Mostly called right before a redirect; `keepalive` lets the request outlive the page.
    window.logWebpageActivity = function (activity) {
      fetch('/userapi/logWebpageActivity', {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify(activity),
      }).then((response) => {
        if (!response.ok) console.error(`logWebpageActivity failed: ${response.status}`);
      }).catch((err) => console.error(err));
    };
  }

  /**
   * Execute all registered initialization tasks.
   * @returns {Promise} Promise that resolves when all tasks complete.
   * @private
   */
  async _executeInitTasks() {
    for (const task of this.initTasks) {
      try {
        const result = task.taskFunction();

        // Handle both synchronous and asynchronous tasks.
        if (result && typeof result.then === 'function') {
          await result;
        }
      } catch (error) {
        console.error(`Init task '${task.name}' failed:`, error);
        throw new Error(`Initialization failed at task: ${task.name}`, { cause: error });
      }
    }
  }

  /**
   * Execute all queued ready callbacks.
   * @private
   */
  _executeReadyCallbacks() {
    while (this.readyCallbacks.length > 0) {
      const callback = this.readyCallbacks.shift();
      try {
        callback();
      } catch (error) {
        console.error('Error executing app ready callback:', error);
      }
    }
  }
}

// Create and export a singleton instance.
window.appManager = new AppManager();
